import axios from 'axios';

// Automatically detect local development server when running on localhost
const getBaseUrl = () => {
  if (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
    return 'http://localhost:8000';
  }
  return process.env.REACT_APP_API_URL || 'https://server2.arthroscopydoc.com';
};

const apiClient = axios.create({
  baseURL: getBaseUrl(),
  timeout: 10000,
});

// Request interceptor: attach token for admin requests if present
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('dr_sunil_admin_token');
  if (token) {
    config.headers.Authorization = `Token ${token}`;
  }
  return config;
});

// Response interceptor for consistent error extraction
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const customError = {
      message: 'Network error or server unavailable. Please try again.',
      status: error.response?.status,
      data: error.response?.data,
    };
    if (error.response?.data) {
      if (typeof error.response.data === 'string') {
        customError.message = error.response.data;
      } else if (typeof error.response.data === 'object') {
        const firstKey = Object.keys(error.response.data)[0];
        const val = error.response.data[firstKey];
        customError.message = Array.isArray(val) ? `${firstKey}: ${val[0]}` : String(val);
      }
    }
    return Promise.reject(customError);
  }
);

export const api = {
  // Public Articles
  getArticles: async () => {
    const res = await apiClient.get('/api/articles/');
    return res.data?.results ? res.data.results : res.data;
  },
  getArticle: async (idOrSlug) => {
    const res = await apiClient.get(`/api/articles/${idOrSlug}/`);
    return res.data;
  },

  // Public Gallery
  getGallery: async () => {
    const res = await apiClient.get('/api/gallery/');
    return res.data?.results ? res.data.results : res.data;
  },

  // Public Useful Links
  getUsefulLinks: async () => {
    const res = await apiClient.get('/api/usefullinks/');
    return res.data?.results ? res.data.results : res.data;
  },

  // Public Reviews
  getReviews: async () => {
    try {
      const res = await apiClient.get('/api/reviews/');
      return res.data?.results ? res.data.results : res.data;
    } catch {
      return [];
    }
  },

  // Public Booking & Contact
  createAppointment: async (appointmentData) => {
    const res = await apiClient.post('/api/appointments/', appointmentData);
    return res.data;
  },
  createContact: async (contactData) => {
    const res = await apiClient.post('/api/contact/', contactData);
    return res.data;
  },

  // --------------------------------------------------------------------------
  // Authenticated Doctor & Management Admin API
  // --------------------------------------------------------------------------
  login: async (username, password) => {
    const res = await apiClient.post('/api/admin/token/', { username, password });
    return res.data; // { token: '...' }
  },

  getAdminMe: async () => {
    const res = await apiClient.get('/api/admin/me/');
    return res.data;
  },

  getAdminStats: async () => {
    const res = await apiClient.get('/api/admin/stats/');
    return res.data;
  },

  // Admin Patients Directory
  getAdminPatients: async (params = {}) => {
    const res = await apiClient.get('/api/admin/patients/', { params });
    return res.data;
  },

  // Admin Appointments
  getAdminAppointments: async (params = {}) => {
    const res = await apiClient.get('/api/admin/appointments/', { params });
    return res.data;
  },
  updateAppointment: async (id, data) => {
    const res = await apiClient.patch(`/api/admin/appointments/${id}/`, data);
    return res.data;
  },
  deleteAppointment: async (id) => {
    const res = await apiClient.delete(`/api/admin/appointments/${id}/`);
    return res.data;
  },

  // Admin Contacts / Inquiries
  getAdminContacts: async (params = {}) => {
    const res = await apiClient.get('/api/admin/contacts/', { params });
    return res.data;
  },
  updateContact: async (id, data) => {
    const res = await apiClient.patch(`/api/admin/contacts/${id}/`, data);
    return res.data;
  },
  deleteContact: async (id) => {
    const res = await apiClient.delete(`/api/admin/contacts/${id}/`);
    return res.data;
  },

  // Admin Articles
  createArticle: async (articleData) => {
    const isFormData = articleData instanceof FormData;
    const config = isFormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {};
    const res = await apiClient.post('/api/admin/articles/', articleData, config);
    return res.data;
  },
  updateArticle: async (id, articleData) => {
    const isFormData = articleData instanceof FormData;
    const config = isFormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {};
    const res = await apiClient.patch(`/api/admin/articles/${id}/`, articleData, config);
    return res.data;
  },
  deleteArticle: async (id) => {
    const res = await apiClient.delete(`/api/admin/articles/${id}/`);
    return res.data;
  },

  // Admin Gallery
  createGalleryItem: async (galleryData) => {
    const isFormData = galleryData instanceof FormData;
    const config = isFormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {};
    const res = await apiClient.post('/api/admin/gallery/', galleryData, config);
    return res.data;
  },
  deleteGalleryItem: async (id) => {
    const res = await apiClient.delete(`/api/admin/gallery/${id}/`);
    return res.data;
  },

  // Admin Reviews
  getAdminReviews: async (params = {}) => {
    const res = await apiClient.get('/api/admin/reviews/', { params });
    return res.data;
  },
  updateReview: async (id, reviewData) => {
    const res = await apiClient.patch(`/api/admin/reviews/${id}/`, reviewData);
    return res.data;
  },
  deleteReview: async (id) => {
    const res = await apiClient.delete(`/api/admin/reviews/${id}/`);
    return res.data;
  },

  // Admin Recycle Bin / Trash & Restore
  getAdminTrash: async (params = {}) => {
    const res = await apiClient.get('/api/admin/trash/', { params });
    return res.data;
  },
  restoreAdminTrashItem: async (type, id) => {
    const res = await apiClient.post('/api/admin/trash/restore/', { type, id });
    return res.data;
  },
  purgeAdminTrashItem: async (type, id) => {
    const res = await apiClient.post('/api/admin/trash/purge/', { type, id });
    return res.data;
  },
};

export default apiClient;
